import { AlertTriangle, RefreshCw, Home, HelpCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface FullPageErrorProps {
  error?: Error;
  onRetry?: () => void;
  onReload?: () => void;
  onGoHome?: () => void;
}

export const FullPageError = ({ 
  error, 
  onRetry, 
  onReload, 
  onGoHome 
}: FullPageErrorProps) => {
  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="max-w-md w-full text-center space-y-6">
        {/* Error Icon */}
        <div className="mx-auto w-20 h-20 bg-destructive/10 rounded-full flex items-center justify-center">
          <AlertTriangle className="w-10 h-10 text-destructive" aria-hidden="true" />
        </div>

        {/* Error Message */}
        <div className="space-y-2">
          <h1 className="text-2xl font-bold text-foreground">
            Algo deu errado
          </h1>
          <p className="text-muted-foreground">
            Ocorreu um erro inesperado. Não se preocupe, seus dados estão seguros.
          </p>
        </div>

        {/* Error Details (collapsible) */}
        {error && (
          <details className="text-left bg-muted/50 rounded-lg p-4">
            <summary className="cursor-pointer text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
              Detalhes técnicos
            </summary>
            <pre className="mt-3 text-xs text-muted-foreground whitespace-pre-wrap break-words font-mono bg-background/50 p-3 rounded border">
              {error.message}
              {error.stack && (
                <>
                  {'\n\n'}
                  {error.stack.split('\n').slice(0, 5).join('\n')}
                </>
              )}
            </pre>
          </details>
        )}

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          {onRetry && (
            <Button 
              variant="default" 
              onClick={onRetry}
              className="gap-2"
              aria-label="Tentar novamente"
            >
              <RefreshCw className="w-4 h-4" aria-hidden="true" />
              Tentar Novamente
            </Button>
          )}
          
          {onGoHome && (
            <Button 
              variant="outline" 
              onClick={onGoHome}
              className="gap-2"
              aria-label="Voltar para página inicial"
            >
              <Home className="w-4 h-4" aria-hidden="true" />
              Página Inicial
            </Button>
          )}
        </div>

        {onReload && (
          <Button 
            variant="ghost" 
            onClick={onReload}
            className="text-sm text-muted-foreground"
            aria-label="Recarregar a página"
          >
            Recarregar Página
          </Button>
        )}

        {/* Help Link */}
        <div className="pt-4 border-t">
          <a 
            href="/ajuda-e-suporte" 
            className="inline-flex items-center gap-2 text-sm text-primary hover:underline"
          >
            <HelpCircle className="w-4 h-4" aria-hidden="true" />
            Precisa de ajuda?
          </a>
        </div>
      </div>
    </div>
  );
};
